import { RecommendationType, RecommendationTheme } from './RecommendationCardTypes';

/**
 * Color themes for different recommendation types
 * Extracted from SuggestedGoals (amber/yellow) and SuggestedResearch (green/emerald)
 */
export const recommendationThemes: Record<RecommendationType, RecommendationTheme> = {
  task: {
    // Amber/Yellow theme from SuggestedGoals
    background: 'from-amber-50/60 to-yellow-50/60 dark:from-amber-950/20 dark:to-yellow-900/20',
    border: 'border-amber-100/50 dark:border-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
    icon: 'text-amber-500 dark:text-amber-400',
    button: 'from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600',
    separator: 'bg-amber-100 dark:bg-amber-900/30',
    hover: 'hover:bg-amber-50/50 dark:hover:bg-amber-900/20'
  },
  research: {
    // Green/Emerald theme from SuggestedResearch
    background: 'from-green-50/60 to-emerald-50/60 dark:from-green-950/20 dark:to-emerald-900/20',
    border: 'border-green-100/50 dark:border-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    icon: 'text-green-500 dark:text-green-400',
    button: 'from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600',
    separator: 'bg-green-100 dark:bg-green-900/30',
    hover: 'hover:bg-green-50/50 dark:hover:bg-green-900/20'
  },
  page: {
    // Purple/Violet theme for pages
    background: 'from-purple-50/60 to-violet-50/60 dark:from-purple-950/20 dark:to-violet-900/20',
    border: 'border-purple-100/50 dark:border-purple-900/30',
    text: 'text-purple-600 dark:text-purple-400',
    icon: 'text-purple-500 dark:text-purple-400',
    button: 'from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600',
    separator: 'bg-purple-100 dark:bg-purple-900/30',
    hover: 'hover:bg-purple-50/50 dark:hover:bg-purple-900/20'
  },
  image: {
    // Blue/Sky theme for images
    background: 'from-blue-50/60 to-sky-50/60 dark:from-blue-950/20 dark:to-sky-900/20',
    border: 'border-blue-100/50 dark:border-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    icon: 'text-blue-500 dark:text-blue-400',
    button: 'from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600',
    separator: 'bg-blue-100 dark:bg-blue-900/30',
    hover: 'hover:bg-blue-50/50 dark:hover:bg-blue-900/20'
  },
  project: {
    // Rose/Pink theme for projects
    background: 'from-rose-50/60 to-pink-50/60 dark:from-rose-950/20 dark:to-pink-900/20',
    border: 'border-rose-100/50 dark:border-rose-900/30',
    text: 'text-rose-600 dark:text-rose-400',
    icon: 'text-rose-500 dark:text-rose-400',
    button: 'from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600',
    separator: 'bg-rose-100 dark:bg-rose-900/30',
    hover: 'hover:bg-rose-50/50 dark:hover:bg-rose-900/20'
  }
};

/**
 * Get theme configuration for a recommendation type
 */
export const getRecommendationTheme = (type: RecommendationType): RecommendationTheme => {
  return recommendationThemes[type];
};

/**
 * Generate CSS classes for recommendation card container
 */
export const getCardContainerClasses = (type: RecommendationType): string => {
  const theme = getRecommendationTheme(type);
  return `
    p-3 rounded-lg 
    bg-gradient-to-br ${theme.background} 
    backdrop-blur-md border ${theme.border} 
    ${theme.hover} transition-colors
  `.trim().replace(/\s+/g, ' ');
};

/**
 * Generate CSS classes for recommendation card icon
 */
export const getCardIconClasses = (type: RecommendationType): string => {
  const theme = getRecommendationTheme(type);
  return `${theme.icon}`;
};

/**
 * Generate CSS classes for recommendation card text
 */
export const getCardTextClasses = (type: RecommendationType): string => {
  const theme = getRecommendationTheme(type);
  return `${theme.text}`;
};

/**
 * Generate CSS classes for recommendation card button
 */
export const getCardButtonClasses = (type: RecommendationType): string => {
  const theme = getRecommendationTheme(type);
  return `
    w-6 h-6 rounded-full 
    bg-gradient-to-r ${theme.button} 
    text-white shadow-sm
    transition-all duration-200
    disabled:opacity-50 disabled:cursor-not-allowed
  `.trim().replace(/\s+/g, ' ');
};

/**
 * Generate CSS classes for recommendation card separator
 */
export const getCardSeparatorClasses = (type: RecommendationType): string => {
  const theme = getRecommendationTheme(type);
  return `${theme.separator}`;
}; 